import {isIdpManaged} from '../identity-provider'
import type {CourseAttrs} from './../models/course'
import {isCourseArchived, isCourseHidden} from './../models/course'

type AllCoursesTagT = 'all-courses'
type ActiveAndHiddenCoursesTagT = 'active-and-hidden'
type ActiveAndVisibleCoursesTagT = 'active-and-visible'
type ArchievedOnlyCoursesTagT = 'archived-only'
type HiddenOnlyCoursesTagT = 'hidden-only'

export type CoursesTagT =
  | AllCoursesTagT
  | ActiveAndHiddenCoursesTagT
  | ActiveAndVisibleCoursesTagT
  | ArchievedOnlyCoursesTagT
  | HiddenOnlyCoursesTagT

// All courses including archived and hidden courses
export type AllCoursesT<T> = {
  tag: 'all-courses'
  courses: Array<T>
}
export function makeAllCourses<T>(courses: Array<T>): AllCoursesT<T> {
  return {tag: 'all-courses', courses}
}

// Active courses, including hidden courses
export type ActiveHiddenCoursesT<T> = {
  tag: 'active-and-hidden'
  courses: Array<T>
}
export function makeActiveHiddenCourses<T>(courses: Array<T>): ActiveHiddenCoursesT<T> {
  return {tag: 'active-and-hidden', courses}
}

// Active courses, not including hidden courses
export type ActiveVisibleCoursesT<T> = {
  tag: 'active-and-visible'
  courses: Array<T>
}
function makeActiveVisibleCourses<T>(courses: Array<T>): ActiveVisibleCoursesT<T> {
  return {tag: 'active-and-visible', courses}
}

// Only archived courses, regardless of hidden status
type ArchivedOnlyCoursesT<T> = {
  tag: 'archived-only'
  courses: Array<T>
}

function makeArchivedOnlyCourses<T>(courses: Array<T>): ArchivedOnlyCoursesT<T> {
  return {tag: 'archived-only', courses}
}

// Only hidden courses, not including archived courses
export type HiddenOnlyCoursesT<T> = {
  tag: 'hidden-only'
  courses: Array<T>
}
function makeHiddenOnlyCourses<T>(courses: Array<T>): HiddenOnlyCoursesT<T> {
  return {tag: 'hidden-only', courses}
}

// Utility functions

export function getActiveCourses<Course extends Pick<CourseAttrs, 'archived'>>(
  courses: Array<Course>
): ActiveHiddenCoursesT<Course> {
  const filteredCourses = courses.filter(c => !isCourseArchived(c))
  return makeActiveHiddenCourses<Course>(filteredCourses)
}

export function getArchivedCourses<Course extends Pick<CourseAttrs, 'archived'>>(
  courses: Array<Course>
): ArchivedOnlyCoursesT<Course> {
  const filteredCourses = courses.filter(c => isCourseArchived(c))
  return makeArchivedOnlyCourses<Course>(filteredCourses)
}

export function getActiveAndVisibleCourses<Course extends Pick<CourseAttrs, 'archived' | 'hidden'>>(
  courses: Array<Course>
): ActiveVisibleCoursesT<Course> {
  const filteredCourses = courses.filter(c => !isCourseArchived(c) && !isCourseHidden(c))
  return makeActiveVisibleCourses<Course>(filteredCourses)
}

export function getActiveAndHiddenCourses<Course extends Pick<CourseAttrs, 'archived' | 'hidden'>>(
  courses: Array<Course>
): HiddenOnlyCoursesT<Course> {
  const filteredCourses = courses.filter(c => !isCourseArchived(c) && isCourseHidden(c))
  return makeHiddenOnlyCourses<Course>(filteredCourses)
}

export function getModifiableCourses<
  Course extends Pick<CourseAttrs, 'archived' | 'hidden' | 'idpManaged'>
>(courses: Array<Course>): Array<Course> {
  return getActiveAndVisibleCourses(courses).courses.filter(course => !isIdpManaged(course))
}
